/**
 * KIAN-Antwortvorschläge am Ticket (v1.402.0, Entscheidung Kevin 27.10.2026).
 *
 * KIAN schreibt über die API eine Antwort an den Kunden als INTERNEN Kommentar ins Ticket — sie geht
 * erst raus, wenn hier jemand „Öffentlich stellen“ klickt. Dann legt der Server einen NEUEN
 * öffentlichen Kommentar mit dem Text ohne Hinweiszeile an (weclapp schickt die Kommentar-Mail), der
 * interne bleibt stehen. Genau einmal: Ein zweiter Klick bekommt 409. Berechtigt ist, wer öffentliche
 * Kommentare schreiben darf — jeder angemeldete Mitarbeiter (wie das Kommentarfeld).
 * Server: backend/src/services/kianReplyProposals.ts.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Checkbox, Group, Modal, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconRobot, IconSend, IconTrash } from '@tabler/icons-react';
import { api, ApiError } from '../lib/api';
import { KIAN_REPLY_PROPOSAL_MARKER, type KianReplyProposal } from '../lib/api/domains/weclapp';
import { ExternalRecipientsList } from './ExternalRecipientsList';

/** Ist dieser (interne) Kommentar ein KIAN-Antwortvorschlag? Für das Etikett in der Kommentarliste. */
export function isKianReplyProposalComment(text: string | null | undefined): boolean {
  return !!text && text.includes(KIAN_REPLY_PROPOSAL_MARKER);
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Meldung nach einem Fehler beim Öffentlich-Stellen (Prüfbericht Runde 7 N-3): „Nicht gesendet“ nur,
 * wenn der Server das weiß. Netzfehler und 5xx können nach dem Senden passiert sein.
 */
export function publishErrorNotice(err: unknown): { color: string; title: string; message: string } {
  const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
  if (err instanceof ApiError) {
    if (err.code === 'REJECTED') return { color: 'red', title: 'Nicht gesendet — weclapp lehnt ab', message };
    if (err.code === 'ALREADY_SENT') return { color: 'blue', title: 'Schon gesendet', message };
    if (err.code === 'OUTCOME_UNKNOWN' || err.code === 'DUPLICATE_RISK') return { color: 'yellow', title: 'Ausgang unklar', message };
    if (err.status != null && err.status >= 400 && err.status < 500) return { color: 'red', title: 'Nicht gesendet', message };
  }
  return { color: 'yellow', title: 'Ausgang unklar', message: 'Keine Antwort vom Server — die Antwort kann schon beim Kunden sein. Bitte im Ticket prüfen.' };
}

const queryKey = (ticketId: string) => ['kian-reply-proposals', ticketId] as const;

export function KianReplyProposals({ ticketId }: { ticketId: string }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: queryKey(ticketId),
    queryFn: () => api.getKianReplyProposals(ticketId),
    staleTime: 30_000,
  });
  const [confirm, setConfirm] = useState<KianReplyProposal | null>(null);
  const [duplicateRisk, setDuplicateRisk] = useState(false);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: queryKey(ticketId) });
    queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', ticketId] });
  };

  const publish = useMutation({
    mutationFn: (p: KianReplyProposal) =>
      api.publishKianReplyProposal(ticketId, p.id, { confirmDuplicateRisk: p.status === 'UNKNOWN' && duplicateRisk }),
    onSuccess: (res) => {
      setConfirm(null);
      setDuplicateRisk(false);
      notifications.show(
        res.status === 'UNKNOWN'
          ? {
              color: 'yellow',
              title: 'Ausgang unklar',
              message: 'weclapp hat nicht geantwortet — die Antwort kann schon beim Kunden sein. Bitte im Ticket prüfen.',
            }
          : res.pending
            ? {
                color: 'yellow',
                title: 'Gespeichert, wird nachgereicht',
                message: 'weclapp ist gerade nicht erreichbar — die Antwort geht automatisch raus, sobald weclapp sie annimmt.',
              }
            : { color: 'green', title: 'Öffentlich gestellt', message: 'Die Antwort steht als öffentlicher Kommentar im Ticket.' },
      );
      refresh();
    },
    onError: (err) => {
      notifications.show(publishErrorNotice(err));
      if (err instanceof ApiError && err.code !== 'REJECTED') setConfirm(null);
      refresh();
    },
  });

  const discard = useMutation({
    mutationFn: (p: KianReplyProposal) => api.discardKianReplyProposal(ticketId, p.id),
    onSuccess: refresh,
    onError: (err) =>
      notifications.show({ color: 'red', title: 'Nicht verworfen', message: err instanceof Error ? err.message : 'Unbekannter Fehler' }),
  });

  const proposals = data?.proposals ?? [];
  const open = proposals.filter((p) => p.status === 'PROPOSED' || p.status === 'UNKNOWN' || p.status === 'SENDING');
  const done = proposals.filter((p) => p.status === 'SENT' || p.status === 'DISCARDED').slice(0, 3);
  if (open.length === 0 && done.length === 0) return null;

  return (
    <Stack gap="xs" mt="xs">
      {open.map((p) => (
        <Paper key={p.id} withBorder p="sm" radius="sm" style={{ borderColor: 'var(--mantine-color-grape-6)', borderLeftWidth: 3 }}>
          <Group gap="xs" mb={6}>
            <IconRobot size={16} color="var(--mantine-color-grape-4)" />
            <Text size="sm" fw={600}>
              Antwortvorschlag von KIAN
            </Text>
            {p.status === 'PROPOSED' && (
              <Badge size="xs" color="grape" variant="light">
                Noch nicht beim Kunden
              </Badge>
            )}
            {p.status === 'UNKNOWN' && (
              <Badge size="xs" color="yellow" variant="light">
                Ausgang unklar
              </Badge>
            )}
            {p.status === 'SENDING' && (
              <Badge size="xs" color="blue" variant="light">
                Wird gesendet …
              </Badge>
            )}
            <Text size="xs" c="dimmed">
              {p.proposedBy ? `${p.proposedBy} · ` : ''}
              {formatWhen(p.createdAt)}
            </Text>
          </Group>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {p.body}
          </Text>
          {p.lastError && p.status !== 'PROPOSED' && (
            <Text size="xs" c="yellow.5" mt={4}>
              {p.lastError}
            </Text>
          )}
          {p.lastError && p.status === 'PROPOSED' && (
            <Text size="xs" c="red.4" mt={4}>
              Letzter Versuch abgelehnt: {p.lastError}
            </Text>
          )}
          <Group gap="xs" mt="sm">
            <Button
              size="xs"
              color="blue"
              leftSection={<IconSend size={14} />}
              disabled={p.status === 'SENDING'}
              onClick={() => {
                setDuplicateRisk(false);
                setConfirm(p);
              }}
            >
              Öffentlich stellen (an Kunden senden)
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<IconTrash size={14} />}
              disabled={p.status === 'SENDING'}
              loading={discard.isPending && discard.variables?.id === p.id}
              onClick={() => {
                if (window.confirm('Antwortvorschlag verwerfen? Er lässt sich danach nicht mehr öffentlich stellen.')) discard.mutate(p);
              }}
            >
              Verwerfen
            </Button>
          </Group>
        </Paper>
      ))}
      {done.map((p) => (
        <Text key={p.id} size="xs" c="dimmed">
          {p.status === 'SENT'
            ? `KIAN-Antwortvorschlag vom ${formatWhen(p.createdAt)} öffentlich gestellt am ${formatWhen(p.sentAt)}${
                p.sentByName ? ` von ${p.sentByName}` : ''
              }${p.sentPending ? ' — wartet noch auf weclapp' : ''}.`
            : `KIAN-Antwortvorschlag vom ${formatWhen(p.createdAt)} verworfen am ${formatWhen(p.discardedAt)}.`}
        </Text>
      ))}

      <Modal
        opened={!!confirm}
        onClose={() => (publish.isPending ? undefined : setConfirm(null))}
        title={<Text fw={600}>Antwort an den Kunden senden?</Text>}
        size="lg"
        zIndex={800}
        centered
      >
        {confirm && (
          <Stack gap="md">
            <Alert color="red" variant="light" p="xs" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">
                Die Antwort wird als <strong>öffentlicher Kommentar</strong> ins Ticket geschrieben —{' '}
                <strong>weclapp schickt dem Kunden dann die Kommentar-Mail</strong>. Der interne Vorschlag bleibt
                als Nachweis stehen; die Hinweiszeile geht nicht mit.
              </Text>
            </Alert>
            <Paper withBorder p="sm" radius="sm">
              <Text size="xs" c="dimmed" mb={4}>
                Text, der rausgeht:
              </Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {confirm.body}
              </Text>
            </Paper>
            <ExternalRecipientsList ticketId={ticketId} />
            {confirm.status === 'UNKNOWN' && (
              <Checkbox
                color="red"
                label="Ich habe im Ticket nachgesehen: Die Antwort ist noch nicht beim Kunden — trotzdem senden."
                checked={duplicateRisk}
                onChange={(e) => {
                  const __checked = e.currentTarget.checked;
                  setDuplicateRisk(__checked);
                }}
              />
            )}
            <Group justify="flex-end" gap="xs">
              <Button variant="default" onClick={() => setConfirm(null)} disabled={publish.isPending}>
                Abbrechen
              </Button>
              <Button
                color="blue"
                leftSection={<IconSend size={14} />}
                loading={publish.isPending}
                disabled={confirm.status === 'UNKNOWN' && !duplicateRisk}
                onClick={() => publish.mutate(confirm)}
              >
                An Kunden senden
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
